//@flow
import React, {useState, useEffect, useCallback} from 'react';
import {makeStyles, Tabs, Tab, TextField, Typography} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useTrainBotActions} from '../../hooks';
import {useToastActions} from '../../../toast-alert/utils';
import type {
  StoreChatbot,
  TrainedMessageAttachment,
  TrainedMessage,
} from '../../../../entities';
import FAQItem from './FAQItem';
import {colors} from '../../../../assets/styles/colors';
import {uploadFileService} from '../../../file-storage/promises';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

const useStyles = makeStyles(theme => ({
  tabsContainer: {
    borderBottom: `1px solid ${colors.grey300}`,
    marginBottom: '1rem',
  },
  tab: {
    textTransform: 'none',
    fontWeight: 500,
  },
  faqContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
    width: '100%',
    maxHeight: '500px',
    overflowY: 'auto',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  addQuestionButton: {
    color: colors.grey600,
    border: `1px dashed ${colors.grey600}`,
    marginBottom: '1rem',
    gridColumn: '1 / -1',
  },
  bulkContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  bulkTextarea: {
    width: '100%',
    minHeight: '320px',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  helpText: {
    fontSize: '0.85rem',
    color: colors.grey600,
    marginBottom: '0.5rem',
    lineHeight: 1.5,
  },
  exampleBox: {
    backgroundColor: colors.grey50,
    padding: '0.75rem',
    borderRadius: '4px',
    border: `1px solid ${colors.grey300}`,
    marginBottom: '1rem',
  },
  exampleTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.grey700,
    marginBottom: '0.5rem',
  },
  exampleText: {
    fontSize: '0.8rem',
    color: colors.grey600,
    fontFamily: 'monospace',
    whiteSpace: 'pre-line',
  },
  sectionFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  submitButton: {
    maxWidth: '200px',
    padding: '0.5rem 1rem',
  },
  cancelButton: {
    maxWidth: '200px',
    padding: '0.5rem 1rem',
  },
}));

// Local type for pending file uploads (not yet uploaded)
type PendingAttachment = {
  file: File,
  error?: string,
};

// QuestionAnswer type for local state management
export type QuestionAnswer = {
  question: string,
  answer: string,
  attachments?: TrainedMessageAttachment[],
  pendingAttachments?: PendingAttachment[],
  // Legacy fields for backward compatibility
  file_id?: string,
  fileId?: string,
};

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
  initialQuestionAnswers: Array<TrainedMessage>,
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_ATTACHMENTS_PER_FAQ = 3;

// FAQ parsing patterns
const FAQ_PATTERNS = {
  QUESTION: /^P:|^Pregunta:|^Q:/i,
  ANSWER: /^R:|^Respuesta:|^A:/i,
};

const FAQ_PREFIXES = {
  QUESTION: ['P:', 'Pregunta:', 'Q:'],
  ANSWER: ['R:', 'Respuesta:', 'A:'],
};

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];

const getResourceTypeFromFile = (
  file: File
): 'PDF' | 'IMAGE' | 'XLSX' | 'VIDEO' | 'AUDIO' => {
  const extension = file.name
    .split('.')
    .pop()
    ?.toLowerCase();

  if (extension === 'pdf') {
    return 'PDF';
  }
  if (extension === 'xlsx' || extension === 'xls') {
    return 'XLSX';
  }
  if (VIDEO_EXTENSIONS.includes(extension || '')) {
    return 'VIDEO';
  }
  if (AUDIO_EXTENSIONS.includes(extension || '')) {
    return 'AUDIO';
  }
  // jpg, jpeg, png, gif, webp, avif
  return 'IMAGE';
};

const getAttachmentTypeFromResourceType = (
  resourceType: 'PDF' | 'IMAGE' | 'XLSX' | 'VIDEO' | 'AUDIO'
): 'document' | 'image' | 'video' | 'audio' => {
  if (resourceType === 'IMAGE') {
    return 'image';
  }
  if (resourceType === 'VIDEO') {
    return 'video';
  }
  if (resourceType === 'AUDIO') {
    return 'audio';
  }
  return 'document';
};

const normalizeFaqItem = (item: TrainedMessage): QuestionAnswer => {
  // Handle legacy file_id conversion to attachments
  let attachments = item.attachments || [];

  // If has legacy file_id but no attachments, create one
  if (item.file_id && attachments.length === 0) {
    attachments = [
      {
        file_id: item.file_id,
        type: 'document',
        url: '',
        name: '',
        mime_type: '',
      },
    ];
  }

  return {
    question: item.question,
    answer: item.answer,
    attachments,
    pendingAttachments: [],
  };
};

const normalizeFaqList = (
  items: Array<TrainedMessage> = []
): Array<QuestionAnswer> => items.map(normalizeFaqItem);

const TrainingTabFaq = React.memo(function TrainingTabFaq(props: Props) {
  const classes = useStyles();
  const {showToast} = useToastActions();
  const {trainBot, loading} = useTrainBotActions();

  const [activeTab, setActiveTab] = useState(
    props.initialQuestionAnswers.length > 0 ? 1 : 0
  );
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([]);
  const [bulkText, setBulkText] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadResultsForConfirm, setUploadResultsForConfirm] = useState<{
    failed: Array<{question: string, error: string}>,
    messages: Array<{
      question: string,
      answer: string,
      attachments?: TrainedMessageAttachment[],
    }>,
  } | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    if (props.initialQuestionAnswers.length === 0) {
      setQuestionAnswers([{question: '', answer: ''}]);
      setActiveTab(0);
    } else {
      const faqItems = normalizeFaqList(props.initialQuestionAnswers);
      setQuestionAnswers(faqItems);
      setActiveTab(1);
    }
    setHasChanges(false);
  }, [props.initialQuestionAnswers]);

  const handleAddQuestion = useCallback(() => {
    setQuestionAnswers(prev => [...prev, {question: '', answer: ''}]);
    setHasChanges(true);
  }, []);

  const handleChangeQuestion = useCallback((index, value) => {
    setQuestionAnswers(prev => {
      const newQuestionAnswers = [...prev];
      newQuestionAnswers[index] = {
        ...newQuestionAnswers[index],
        question: value,
      };
      return newQuestionAnswers;
    });
    setHasChanges(true);
  }, []);

  const handleChangeAnswer = useCallback((index, value) => {
    setQuestionAnswers(prev => {
      const newQuestionAnswers = [...prev];
      newQuestionAnswers[index] = {
        ...newQuestionAnswers[index],
        answer: value,
      };
      return newQuestionAnswers;
    });
    setHasChanges(true);
  }, []);

  const handleFileChange = useCallback(
    (index, file: File | null) => {
      if (!file) return;

      // Check if it's a non-ogg audio file and show warning
      const extension = file.name
        .split('.')
        .pop()
        ?.toLowerCase();
      const isAudio = AUDIO_EXTENSIONS.includes(extension || '');
      if (isAudio && extension !== 'ogg') {
        showToast({
          message:
            'Los archivos de audio que no son .ogg pueden no enviarse correctamente en WhatsApp. Te recomendamos convertirlo a .ogg',
          title: 'Formato de audio no recomendado',
          type: 'warning',
          duration: 15000,
          action: {
            label: 'Convertir a OGG',
            onClick: () =>
              window.open('https://convertio.co/es/mp3-ogg/', '_blank'),
          },
        });
      }

      setQuestionAnswers(prev => {
        const newQuestionAnswers = [...prev];
        const currentItem = newQuestionAnswers[index];
        const currentAttachments = currentItem.attachments || [];
        const currentPendingAttachments = currentItem.pendingAttachments || [];
        const totalAttachments =
          currentAttachments.length + currentPendingAttachments.length;

        if (totalAttachments >= MAX_ATTACHMENTS_PER_FAQ) {
          return newQuestionAnswers;
        }

        if (file.size > MAX_FILE_SIZE) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
          const errorAttachment: PendingAttachment = {
            file,
            error: `El archivo es demasiado grande (${sizeMB}MB). El tamaño máximo es ${MAX_FILE_SIZE}MB.`,
          };
          newQuestionAnswers[index] = {
            ...currentItem,
            pendingAttachments: [...currentPendingAttachments, errorAttachment],
          };
        } else {
          const newPendingAttachment: PendingAttachment = {file};
          newQuestionAnswers[index] = {
            ...currentItem,
            pendingAttachments: [
              ...currentPendingAttachments,
              newPendingAttachment,
            ],
          };
        }

        return newQuestionAnswers;
      });
      setHasChanges(true);
    },
    [showToast]
  );

  const handleDeleteQuestion = useCallback(index => {
    setQuestionAnswers(prev => {
      const newQuestionAnswers = [...prev];
      newQuestionAnswers.splice(index, 1);
      return newQuestionAnswers;
    });
    setHasChanges(true);
  }, []);

  const handleAttachmentRemove = useCallback(
    (index: number, attachmentIndex: number) => {
      setQuestionAnswers(prev => {
        const newQuestionAnswers = [...prev];
        const currentItem = newQuestionAnswers[index];
        const currentAttachments = currentItem.attachments || [];

        newQuestionAnswers[index] = {
          ...currentItem,
          attachments: currentAttachments.filter(
            (_, i) => i !== attachmentIndex
          ),
        };
        return newQuestionAnswers;
      });
      setHasChanges(true);
    },
    []
  );

  const handlePendingAttachmentRemove = useCallback(
    (index: number, pendingIndex: number) => {
      setQuestionAnswers(prev => {
        const newQuestionAnswers = [...prev];
        const currentItem = newQuestionAnswers[index];
        const currentPendingAttachments = currentItem.pendingAttachments || [];

        newQuestionAnswers[index] = {
          ...currentItem,
          pendingAttachments: currentPendingAttachments.filter(
            (_, i) => i !== pendingIndex
          ),
        };
        return newQuestionAnswers;
      });
      setHasChanges(true);
    },
    []
  );

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleBulkTextChange = useCallback(event => {
    setBulkText(event.target.value);
    setHasChanges(true);
  }, []);

  const parseBulkText = useCallback((text: string): QuestionAnswer[] => {
    const lines = text.split('\n');
    const faqs: QuestionAnswer[] = [];
    let currentQuestion = '';
    let currentAnswer = '';
    let isReadingAnswer = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!line) {
        if (currentQuestion && currentAnswer) {
          faqs.push({
            question: currentQuestion.trim(),
            answer: currentAnswer.trim(),
          });
          currentQuestion = '';
          currentAnswer = '';
          isReadingAnswer = false;
        }
        continue;
      }

      if (line.match(FAQ_PATTERNS.QUESTION)) {
        if (currentQuestion && currentAnswer) {
          faqs.push({
            question: currentQuestion.trim(),
            answer: currentAnswer.trim(),
          });
          currentAnswer = '';
        }
        currentQuestion = line.replace(FAQ_PATTERNS.QUESTION, '').trim();
        isReadingAnswer = false;
      } else if (line.match(FAQ_PATTERNS.ANSWER)) {
        currentAnswer = line.replace(FAQ_PATTERNS.ANSWER, '').trim();
        isReadingAnswer = true;
      } else {
        if (isReadingAnswer) {
          currentAnswer += ' ' + line;
        } else if (currentQuestion) {
          currentQuestion += ' ' + line;
        }
      }
    }

    if (currentQuestion && currentAnswer) {
      faqs.push({
        question: currentQuestion.trim(),
        answer: currentAnswer.trim(),
      });
    }

    return faqs;
  }, []);

  const handleImportBulk = useCallback(() => {
    if (!bulkText.trim()) {
      showToast({
        message: 'Por favor ingresa al menos una pregunta y respuesta',
        title: 'Texto requerido',
        type: 'warning',
        duration: 3000,
      });
      return;
    }

    const parsed = parseBulkText(bulkText);

    if (parsed.length === 0) {
      showToast({
        message: `No se pudo detectar el formato. Asegúrate de usar ${FAQ_PREFIXES.QUESTION[0]} y ${FAQ_PREFIXES.ANSWER[0]} para cada pregunta y respuesta`,
        title: 'Formato inválido',
        type: 'warning',
        duration: 4000,
      });
      return;
    }

    setQuestionAnswers(parsed);
    setActiveTab(1);
    setHasChanges(true);

    showToast({
      message: `${parsed.length} pregunta(s) importada(s) correctamente`,
      title: 'Importación exitosa',
      type: 'success',
      duration: 3000,
    });
  }, [bulkText, parseBulkText, showToast]);

  const getButtonText = () => {
    if (uploadingFiles) return 'Subiendo archivos...';
    if (loading) return 'Guardando...';
    return 'Guardar';
  };

  const handleCancel = () => {
    if (props.initialQuestionAnswers.length === 0) {
      setQuestionAnswers([{question: '', answer: ''}]);
    } else {
      const faqItems = normalizeFaqList(props.initialQuestionAnswers);
      setQuestionAnswers(faqItems);
    }
    setBulkText('');
    setHasChanges(false);

    showToast({
      message: 'Cambios descartados',
      title: 'Cancelado',
      type: 'info',
      duration: 2000,
    });
  };

  const handleTrain = async () => {
    if (!props.chatBot?.id) return;

    try {
      const validQuestionAnswers = questionAnswers.filter(
        qa => qa.question.trim() && qa.answer.trim()
      );

      if (validQuestionAnswers.length === 0) {
        showToast({
          message: 'Por favor agrega al menos una pregunta y respuesta válida',
          title: 'Preguntas y respuestas requeridas',
          type: 'warning',
          duration: 3000,
        });
        return;
      }

      const uploadResults = {
        successful: [],
        failed: [],
      };

      const messages = [];

      setUploadingFiles(true);

      for (let i = 0; i < validQuestionAnswers.length; i++) {
        const qa = validQuestionAnswers[i];
        const messageObj: {
          question: string,
          answer: string,
          attachments?: TrainedMessageAttachment[],
        } = {
          question: qa.question,
          answer: qa.answer,
          attachments: [],
        };

        if (qa.attachments && qa.attachments.length > 0) {
          messageObj.attachments = [...qa.attachments];
        }

        const pendingAttachments = qa.pendingAttachments || [];
        const validPendingAttachments = pendingAttachments.filter(
          pa => !pa.error
        );

        if (validPendingAttachments.length > 0) {
          for (let j = 0; j < validPendingAttachments.length; j++) {
            const pendingAttachment = validPendingAttachments[j];
            try {
              showToast({
                message: `Subiendo archivo ${j + 1}/${
                  validPendingAttachments.length
                } para pregunta ${i + 1}...`,
                title: 'Subiendo archivos',
                type: 'info',
                duration: 2000,
              });

              const resourceType = getResourceTypeFromFile(
                pendingAttachment.file
              );
              const uploadedFiles = await uploadFileService(
                pendingAttachment.file,
                {
                  filePrefix: 'winehouse/chatbot/faq',
                  resourceType,
                }
              );

              if (uploadedFiles && uploadedFiles.length > 0) {
                const uploadedFile = uploadedFiles[0];
                const newAttachment: TrainedMessageAttachment = {
                  file_id: uploadedFile.id,
                  type: getAttachmentTypeFromResourceType(resourceType),
                  url: uploadedFile.url,
                  name:
                    uploadedFile.metadata?.originalFilename ||
                    pendingAttachment.file.name,
                  mime_type: pendingAttachment.file.type || '',
                };
                messageObj.attachments = [
                  ...(messageObj.attachments || []),
                  newAttachment,
                ];
                uploadResults.successful.push(qa.question);
              } else {
                uploadResults.failed.push({
                  question: qa.question,
                  error: 'No se pudo subir el archivo',
                });
              }
            } catch (error) {
              const errorMsg = error?.message || 'Error desconocido';
              uploadResults.failed.push({
                question: qa.question,
                error: errorMsg,
              });
            }
          }
        }

        // Clean up empty attachments array
        if (messageObj.attachments && messageObj.attachments.length === 0) {
          delete messageObj.attachments;
        }

        messages.push(messageObj);
      }

      setUploadingFiles(false);

      if (uploadResults.failed.length > 0) {
        const failedCount = uploadResults.failed.length;
        const totalFiles = uploadResults.successful.length + failedCount;

        if (failedCount === totalFiles) {
          showToast({
            message:
              'Todos los archivos fallaron al subir. Por favor, verifica que los archivos sean menores a 1MB e intenta nuevamente.',
            title: 'Error al subir archivos',
            type: 'error',
            duration: 5000,
          });
          return;
        } else {
          setUploadResultsForConfirm({
            failed: uploadResults.failed,
            messages,
          });
          setShowConfirmModal(true);
          return;
        }
      }

      await proceedWithTraining(messages);
    } catch (error) {
      setUploadingFiles(false);
      const errorMessage =
        error?.message || 'Error desconocido durante el entrenamiento';
      showToast({
        message: errorMessage,
        title: 'Error en el entrenamiento',
        type: 'danger',
        duration: 5000,
      });
    }
  };

  const proceedWithTraining = async (
    messages: Array<{
      question: string,
      answer: string,
      attachments?: TrainedMessageAttachment[],
    }>
  ) => {
    try {
      await trainBot(props.chatBot.id, {
        trainingType: 'TRAINED_MESSAGES',
        messages,
      });

      showToast({
        message:
          'Entrenamiento con preguntas y respuestas iniciado correctamente',
        title: 'Entrenamiento iniciado',
        type: 'success',
        duration: 3000,
      });

      setHasChanges(false);
      props.onSaveSuccess();
    } catch (error) {
      const errorMessage =
        error?.message || 'Error desconocido durante el entrenamiento';
      showToast({
        message: errorMessage,
        title: 'Error en el entrenamiento',
        type: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const handleConfirmPartialUpload = async () => {
    setShowConfirmModal(false);
    if (uploadResultsForConfirm) {
      await proceedWithTraining(uploadResultsForConfirm.messages);
    }
    setUploadResultsForConfirm(null);
  };

  const handleCancelPartialUpload = () => {
    setShowConfirmModal(false);
    setUploadResultsForConfirm(null);
  };

  return (
    <>
      <div className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Importación Rápida" className={classes.tab} />
          <Tab label="Edición Individual" className={classes.tab} />
        </Tabs>
      </div>

      {activeTab === 0 && (
        <div className={classes.bulkContainer}>
          <div className={classes.exampleBox}>
            <Typography className={classes.exampleTitle}>
              <span role="img" aria-label="clipboard">
                📋
              </span>{' '}
              Formato sugerido:
            </Typography>
            <Typography className={classes.exampleText}>
              {`${FAQ_PREFIXES.QUESTION[0]} ¿Cuál es el horario de atención?
${FAQ_PREFIXES.ANSWER[0]} Nuestro horario es de lunes a viernes de 9am a 6pm

${FAQ_PREFIXES.QUESTION[0]} ¿Hacen envíos?
${FAQ_PREFIXES.ANSWER[0]} Sí, hacemos envíos a todo el país

${FAQ_PREFIXES.QUESTION[0]} ¿Cuál es el costo de envío?
${FAQ_PREFIXES.ANSWER[0]} El costo varía según la zona, desde $5`}
            </Typography>
          </div>
          <Typography className={classes.helpText}>
            <span role="img" aria-label="lightbulb">
              💡
            </span>{' '}
            <strong>Tip:</strong> Puedes usar{' '}
            {FAQ_PREFIXES.QUESTION.map((prefix, idx) => (
              <span key={prefix}>
                {idx > 0 && ' o '}
                <strong>{prefix}</strong>
              </span>
            ))}{' '}
            para las preguntas, y{' '}
            {FAQ_PREFIXES.ANSWER.map((prefix, idx) => (
              <span key={prefix}>
                {idx > 0 && ' o '}
                <strong>{prefix}</strong>
              </span>
            ))}{' '}
            para las respuestas. Deja una línea en blanco entre cada par de
            pregunta-respuesta.
          </Typography>

          <TextField
            multiline
            rows={15}
            variant="outlined"
            placeholder="Pega aquí tus preguntas y respuestas..."
            value={bulkText}
            onChange={handleBulkTextChange}
            className={classes.bulkTextarea}
            InputProps={{
              style: {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />

          <CustomButton
            variant="contained"
            color="primary"
            onClick={handleImportBulk}
            disabled={!bulkText.trim()}
            fullWidth
          >
            Importar y continuar editando
          </CustomButton>
        </div>
      )}

      {activeTab === 1 && (
        <div className={classes.faqContainer}>
          {questionAnswers.map((item, index) => (
            <FAQItem
              key={index}
              index={index}
              question={item.question}
              answer={item.answer}
              attachments={item.attachments}
              pendingAttachments={item.pendingAttachments}
              maxAttachments={MAX_ATTACHMENTS_PER_FAQ}
              onQuestionChange={handleChangeQuestion}
              onAnswerChange={handleChangeAnswer}
              onFileChange={handleFileChange}
              onAttachmentRemove={handleAttachmentRemove}
              onPendingAttachmentRemove={handlePendingAttachmentRemove}
              onDelete={handleDeleteQuestion}
            />
          ))}
          <CustomButton
            variant="outlined"
            color="default"
            fullWidth
            className={classes.addQuestionButton}
            onClick={handleAddQuestion}
          >
            Agregar pregunta
          </CustomButton>
        </div>
      )}
      <div className={classes.sectionFooter}>
        <CustomButton
          variant="outlined"
          color="default"
          className={classes.cancelButton}
          onClick={handleCancel}
          disabled={loading || !hasChanges}
          fullWidth
        >
          Cancelar
        </CustomButton>
        <CustomButton
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={handleTrain}
          disabled={loading || uploadingFiles}
          fullWidth
        >
          {getButtonText()}
        </CustomButton>
      </div>

      <ConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Algunos archivos no se pudieron subir"
        message={
          uploadResultsForConfirm
            ? `${uploadResultsForConfirm.failed.length} archivo(s) fallaron al subir.\n\n¿Deseas continuar con el entrenamiento usando solo los archivos que se subieron correctamente?`
            : ''
        }
        onConfirm={handleConfirmPartialUpload}
        onCancel={handleCancelPartialUpload}
        confirmButtonLabel="Continuar"
        cancelButtonLabel="Cancelar y revisar"
      />
    </>
  );
});

export default TrainingTabFaq;
